<script setup lang="ts">
import { computed, onMounted, ref, watch, type PropType, type Ref } from 'vue';
import type { DropdownOption, SelectOption } from 'naive-ui';
import { useDebounceFn } from '@vueuse/core';
import {
  addContractualPartyRepresentative,
  getContractManagementContractualParty,
  searchContractManagementContractualParties,
  SignatureType,
  SigningRequestSigneeRole,
  type FilterableSearchQuery,
} from '@/api';
import GaiaAddButton from '../base/GaiaAddButton.vue';
import GaiaAvatar from '../base/GaiaAvatar.vue';
import GaiaBanner from '../composite/GaiaBanner.vue';
import GaiaPagination from '../composite/GaiaPagination.vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaCreateButton from '../base/GaiaCreateButton.vue';
import GaiaLink from '../base/GaiaLink.vue';
import GaiaModal from '../base/GaiaModal.vue';
import GaiaPickerOption from '../composite/GaiaPickerOption.vue';
import GaiaSegmented, { type GaiaSegmentedOption } from '../base/GaiaSegmented.vue';
import GaiaRemoteSelect, {
  type GaiaRemoteFetch,
  type GaiaRemotePage,
} from '../base/selects/GaiaRemoteSelect.vue';
import GaiaSelect from '../base/selects/GaiaSelect.vue';
import GaiaDropdown, { gaiaCheckedIcon, gaiaDangerOption } from '../base/GaiaDropdown.vue';
import GaiaIconButton from '../base/GaiaIconButton.vue';
import {
  IconAdd,
  IconAlert,
  IconChevronDown,
  IconMore,
  IconReorder,
} from '@/components/design/icons';
import GaiaSearchInput from '../base/inputs/GaiaSearchInput.vue';
import GaiaTabs from '../base/GaiaTabs.vue';
import GaiaText from '../base/GaiaText.vue';
import { signatureTypeMap, signeeRoleMap } from '../statusMaps';
import { isSigneeReadyForSubmission, signeeSigner } from '@/composables/useSigningWizard';
import type { SignatureSigneeForm } from '@/types/contractManagement/signatureFlow';
import {
  PARTY_KIND_LABELS,
  preselectedRepresentative,
  searchPartiesOfKind,
  toPartyRow,
  type ContractualParty,
} from '@/services/contractManagement/contractualPartyRow';
import EntityFormDialog from '@/views/contractManagement/EntityFormDialog.vue';
import {
  signingPartyConfig,
  type EntityFormValues,
  type EntityRow,
} from '@/views/contractManagement/entityListConfig';
import { isEnumValue } from '@/utils/enumUtils';
import { extractErrorDetail } from '@/utils/errorHandler';
import { ACCESS_PERMISSION_WRITE, deniedReason, gateOption } from '@/utils/accessPermission';
import { initialsOf } from '@/utils/initials';
import {
  isSignatureParticipantNameValid,
  loadSignatureParticipantNamePattern,
} from '@/utils/signatureParticipantNameValidation';
import { gaiaNeutral, gaiaStatus } from '../themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * Step 3 of the prepare-signature wizard: who signs, who approves, who is
 * copied in. Recipients are the organisation's contractual parties, so this
 * component searches them, and can create or correct one in place. Only
 * natural persons sign: a legal-entity party signs through one of its
 * representatives, picked on its card. The provider needs the signer's email
 * plus a first and last name it accepts, and the backend reads them off the
 * signer's party record — so a signer missing either is fixed on the party.
 */

type PickerTab = 'natural_person' | 'legal_entity';

interface DragState {
  role: SigningRequestSigneeRole;
  originalIndex: number;
}

const props = defineProps({
  form: {
    type: Array as PropType<SignatureSigneeForm[]>,
    required: true,
  },
  orderedSigners: {
    type: Boolean,
    default: false,
  },
  orderedApprovers: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  'update:form': [form: SignatureSigneeForm[]];
  'update:ordered-signers': [value: boolean];
  'update:ordered-approvers': [value: boolean];
}>();

const message = useGaiaToast();

// ── Sections ────────────────────────────────────────────────────────────────
// The page reads like a routing slip: who gets the document, grouped by what
// they do with it, and — per group — whether they get it at once or in turn.
// The role is said once, by the group; every recipient row has one shape.

interface RecipientSection {
  role: SigningRequestSigneeRole;
  title: string;
  /** What the group does, when it isn't ordered (ordered groups say so instead). */
  blurb: string;
  /** Signers and approvers can act in turn; followers only watch. */
  orderable: boolean;
  tourKey?: string;
}

const SECTIONS: RecipientSection[] = [
  {
    role: SigningRequestSigneeRole.SIGNER,
    title: 'Signers',
    blurb: 'Everyone signs at the same time.',
    orderable: true,
    tourKey: 'signing.add-signer',
  },
  {
    role: SigningRequestSigneeRole.APPROVER,
    title: 'Approvers',
    blurb: 'Approve the document before it goes out for signing.',
    orderable: true,
  },
  {
    role: SigningRequestSigneeRole.FOLLOWER,
    title: 'Followers',
    blurb: 'Kept up to date and sent the signed copy. They don’t sign.',
    orderable: false,
  },
];

function orderOptions(role: SigningRequestSigneeRole): GaiaSegmentedOption[] {
  return [
    { label: 'All at once', value: 'parallel' },
    {
      label: 'In order',
      value: 'sequential',
      tourKey: role === SigningRequestSigneeRole.SIGNER ? 'signing.order' : undefined,
    },
  ];
}

function isOrdered(role: SigningRequestSigneeRole): boolean {
  if (role === SigningRequestSigneeRole.SIGNER) return props.orderedSigners;
  if (role === SigningRequestSigneeRole.APPROVER) return props.orderedApprovers;
  return false;
}

function setOrderMode(role: SigningRequestSigneeRole, mode: string): void {
  const ordered = mode === 'sequential';
  if (role === SigningRequestSigneeRole.SIGNER) emit('update:ordered-signers', ordered);
  else emit('update:ordered-approvers', ordered);
}

function sectionBlurb(section: RecipientSection): string {
  if (!isOrdered(section.role)) return section.blurb;
  return section.role === SigningRequestSigneeRole.SIGNER
    ? 'Each signer gets the document once the one before has signed.'
    : 'Each approver gets the document once the one before has approved. It goes out for signing once all have approved.';
}

/** A group's recipients, with their index in the whole form (what the updates take). */
function rowsFor(role: SigningRequestSigneeRole): { signee: SignatureSigneeForm; index: number }[] {
  return props.form
    .map((signee, index) => ({ signee, index }))
    .filter(row => row.signee.role === role);
}

// A signer's level: the short code on the row, the full names in its menu.
// Only SES is live today; AES and QES stay listed (disabled) so the levels
// remain discoverable until the providers support them.
const SIGNATURE_LEVEL: Record<SignatureType, string> = {
  [SignatureType.ELECTRONIC_SIGNATURE]: 'SES',
  [SignatureType.ADVANCED_ELECTRONIC_SIGNATURE]: 'AES',
  [SignatureType.QUALIFIED_ELECTRONIC_SIGNATURE]: 'QES',
};
const AVAILABLE_SIGNATURE_TYPES = new Set<SignatureType>([SignatureType.ELECTRONIC_SIGNATURE]);

function signatureTypeMenu(current: SignatureType): DropdownOption[] {
  return Object.values(SignatureType).map(type => ({
    key: type,
    label: AVAILABLE_SIGNATURE_TYPES.has(type)
      ? signatureTypeMap[type]
      : `${signatureTypeMap[type]} · not available`,
    disabled: !AVAILABLE_SIGNATURE_TYPES.has(type),
    icon: gaiaCheckedIcon(type === current),
  }));
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Row ⋯ menu ──────────────────────────────────────────────────────────────

function rowMenu(index: number, position: number, count: number): DropdownOption[] {
  const signee = props.form[index];
  const ordered = isOrdered(signee.role);
  const options: DropdownOption[] = [];
  const signer = signeeSigner(signee);
  if (signer) {
    options.push(
      gateOption(
        { key: 'edit', label: isLegalEntity(signee) ? 'Edit representative' : 'Edit details' },
        signer.permissions,
        ACCESS_PERMISSION_WRITE
      )
    );
  }
  if (ordered) {
    options.push(
      { key: 'up', label: 'Move up', disabled: position === 0 },
      { key: 'down', label: 'Move down', disabled: position === count - 1 }
    );
  }
  for (const section of SECTIONS) {
    if (section.role !== signee.role) {
      options.push({
        key: `role:${section.role}`,
        label: `Make ${signeeRoleMap[section.role].toLowerCase()}`,
      });
    }
  }
  options.push(
    { type: 'divider', key: 'divider' },
    { key: 'remove', label: 'Remove', ...gaiaDangerOption }
  );
  return options;
}

function handleRowMenu(key: string, index: number): void {
  switch (key) {
    case 'edit': {
      void openEditSigner(index);
      break;
    }
    case 'remove': {
      removeSignee(index);
      break;
    }
    case 'up':
    case 'down': {
      moveWithinRole(index, key === 'up' ? -1 : 1);
      break;
    }
    default: {
      // "role:<role>" — joins the end of its new group, as a new recipient would.
      const role = key.slice('role:'.length);
      if (!isEnumValue(SigningRequestSigneeRole, role)) break;
      updateForm([
        ...props.form.filter((_, current) => current !== index),
        { ...props.form[index], role },
      ]);
    }
  }
}

function moveWithinRole(index: number, by: -1 | 1): void {
  const rows = rowsFor(props.form[index].role);
  const target = rows.findIndex(row => row.index === index) + by;
  if (target < 0 || target >= rows.length) return;
  reorderWithinRole(props.form[index].role, index, rows[target].index);
}

// ── Form updates ────────────────────────────────────────────────────────────

function updateForm(next: SignatureSigneeForm[]): void {
  emit('update:form', next);
}

function updateSignee(index: number, patch: Partial<SignatureSigneeForm>): void {
  updateForm(
    props.form.map((signee, currentIndex) =>
      currentIndex === index ? { ...signee, ...patch } : signee
    )
  );
}

function removeSignee(index: number): void {
  updateForm(props.form.filter((_, currentIndex) => currentIndex !== index));
}

// ── Party read-out ──────────────────────────────────────────────────────────

function isLegalEntity(signee: SignatureSigneeForm): boolean {
  return signee.party?.kind === 'legal_entity';
}

/**
 * Attaching a party only needs READ, but fixing its details is an update of
 * the signing person's record, so it needs WRITE there — the person, not the
 * company they sign for. Null when the caller may.
 */
function signerEditBlock(signee: SignatureSigneeForm): string | null {
  return deniedReason(signeeSigner(signee)?.permissions, ACCESS_PERMISSION_WRITE);
}

/** Adding a representative changes the company, so it needs WRITE on it. */
function representativeAddBlock(signee: SignatureSigneeForm): string | null {
  return deniedReason(signee.party?.permissions, ACCESS_PERMISSION_WRITE);
}

function signerEmail(signee: SignatureSigneeForm): string {
  return signeeSigner(signee)?.email?.trim() ?? '';
}

/**
 * The row's one problem line — what still stops this recipient, in plain
 * words — or null when it is ready. A company without anyone signing for it
 * is asked for that person in the row instead.
 */
function rowIssue(signee: SignatureSigneeForm): string | null {
  if (!signeeSigner(signee)) return null;
  const email = signerEmail(signee);
  if (email && !EMAIL_RE.test(email)) return 'The signing email doesn’t look valid';
  if (isSigneeReadyForSubmission(signee)) return null;
  return `Needs ${joinAnd(missingFieldLabels(signee))}`;
}

function joinAnd(parts: string[]): string {
  return parts.length < 2
    ? (parts[0] ?? '')
    : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
}

/** What still stops this recipient from being submitted. */
function missingFieldLabels(signee: SignatureSigneeForm): string[] {
  const signer = signeeSigner(signee);
  if (!signer) return [isLegalEntity(signee) ? 'a representative who signs' : 'a party'];

  const missing: string[] = [];
  if (!signer.email?.trim()) missing.push('a signing email');

  const firstName = signer.first_name?.trim() ?? '';
  const lastName = signer.last_name?.trim() ?? '';
  if (!firstName || !isSignatureParticipantNameValid(firstName)) {
    missing.push('a usable first name');
  }
  if (!lastName || !isSignatureParticipantNameValid(lastName)) {
    missing.push('a usable last name');
  }
  return missing;
}

// ── Drag reorder (only meaningful while the role is ordered) ─────────────────

const draggedSignee = ref<DragState | null>(null);
const dragOverSignee = ref<DragState | null>(null);

function isDraggable(role: SigningRequestSigneeRole): boolean {
  return isOrdered(role);
}

function isDragTarget(role: SigningRequestSigneeRole, originalIndex: number): boolean {
  return (
    dragOverSignee.value?.role === role && dragOverSignee.value.originalIndex === originalIndex
  );
}

function handleDragStart(
  role: SigningRequestSigneeRole,
  originalIndex: number,
  event: DragEvent
): void {
  if (!isDraggable(role)) return;

  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `${role}:${originalIndex}`);
  }

  // Chromium cancels the drag if the dragged element re-renders inside the
  // dragstart handler, so the state change waits for the next task.
  setTimeout(() => {
    draggedSignee.value = { role, originalIndex };
  }, 0);
}

function handleDragOver(
  role: SigningRequestSigneeRole,
  originalIndex: number,
  event: DragEvent
): void {
  if (!draggedSignee.value || draggedSignee.value.role !== role) return;

  event.preventDefault();
  dragOverSignee.value = { role, originalIndex };

  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
  }
}

function handleDrop(role: SigningRequestSigneeRole, originalIndex: number, event: DragEvent): void {
  if (!draggedSignee.value || draggedSignee.value.role !== role) return;

  event.preventDefault();
  reorderWithinRole(role, draggedSignee.value.originalIndex, originalIndex);
  draggedSignee.value = null;
  dragOverSignee.value = null;
}

function reorderWithinRole(
  role: SigningRequestSigneeRole,
  fromOriginalIndex: number,
  toOriginalIndex: number
): void {
  if (fromOriginalIndex === toOriginalIndex) return;

  const roleItems = props.form
    .map((signee, originalIndex) => ({ signee, originalIndex }))
    .filter(item => item.signee.role === role);

  const fromRoleIndex = roleItems.findIndex(item => item.originalIndex === fromOriginalIndex);
  const toRoleIndex = roleItems.findIndex(item => item.originalIndex === toOriginalIndex);
  if (fromRoleIndex === -1 || toRoleIndex === -1) return;

  const reordered = roleItems.map(item => item.signee);
  const [moved] = reordered.splice(fromRoleIndex, 1);
  reordered.splice(toRoleIndex, 0, moved);

  let nextIndex = 0;
  updateForm(
    props.form.map(signee => {
      if (signee.role !== role) return signee;
      const next = reordered[nextIndex];
      nextIndex += 1;
      return next;
    })
  );
}

// ── Picker ──────────────────────────────────────────────────────────────────

const pickerVisible = ref(false);
const pickerRole = ref<SigningRequestSigneeRole>(SigningRequestSigneeRole.SIGNER);
const activePickerTab = ref<PickerTab>('natural_person');
/**
 * Picks are held as the party records, not just their uids: the pools are
 * paged, so a party ticked on page 1 is no longer in the list by the time the
 * user confirms on page 3.
 */
const picked = ref(new Map<string, ContractualParty>());

/** One searched, paged pool per kind — the two tabs. */
interface PartyPool {
  query: Ref<string>;
  results: Ref<ContractualParty[]>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  page: Ref<number>;
  total: Ref<number>;
  fetch: () => Promise<void>;
}

function usePartyPool(kind: PickerTab): PartyPool {
  const query = ref('');
  const results = ref<ContractualParty[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const page = ref(1);
  const total = ref(0);

  async function fetch(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const body: FilterableSearchQuery = {
        text: query.value,
        limit: PAGE_SIZE,
        offset: (page.value - 1) * PAGE_SIZE,
        sort_key: 'name',
        sort_direction: 'asc',
        similarity_threshold: 0.5,
        column_filters: { kind: [kind] },
      };
      const { data } = await searchContractManagementContractualParties({
        body,
        throwOnError: true,
      });
      results.value = data.results;
      total.value = data.result_count;
    } catch (fetchError) {
      error.value = extractErrorDetail(fetchError, 'Failed to load contractual parties.');
      results.value = [];
      total.value = 0;
    } finally {
      loading.value = false;
    }
  }

  const debounced = useDebounceFn(() => {
    page.value = 1;
    void fetch();
  }, 400);
  watch(query, () => debounced());
  watch(page, () => void fetch());

  return { query, results, loading, error, page, total, fetch };
}

const PAGE_SIZE = 10;
const people = usePartyPool('natural_person');
const companies = usePartyPool('legal_entity');

const pickerTitle = computed(() => `Add ${signeeRoleMap[pickerRole.value].toLowerCase()}s`);
const pickedCount = computed(() => picked.value.size);

function openPicker(role: SigningRequestSigneeRole): void {
  pickerRole.value = role;
  picked.value = new Map();
  people.query.value = '';
  companies.query.value = '';
  people.page.value = 1;
  companies.page.value = 1;
  pickerVisible.value = true;
  void people.fetch();
  void companies.fetch();
}

/** Already on the request in the role being added — nothing to add twice. */
function isAlreadyInRole(uid: string): boolean {
  return props.form.some(signee => signee.role === pickerRole.value && signee.party?.uid === uid);
}

/** On the request in another role — a party can't be signer and approver. */
function isInOtherRole(uid: string): boolean {
  return props.form.some(signee => signee.role !== pickerRole.value && signee.party?.uid === uid);
}

function pickerRowMeta(party: ContractualParty): string {
  const parts: string[] = [];
  if (party.kind === 'natural_person') {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    parts.push(party.email?.trim() || 'No email on record');
  } else {
    const count = party.representatives?.length ?? 0;
    parts.push(
      count === 0 ? 'No representative yet' : `${count} representative${count === 1 ? '' : 's'}`
    );
  }
  if (isAlreadyInRole(party.uid)) parts.push('already added');
  else if (isInOtherRole(party.uid)) parts.push('already in another role');
  return parts.join(' · ');
}

function togglePicked(party: ContractualParty): void {
  if (isAlreadyInRole(party.uid) || isInOtherRole(party.uid)) return;
  const next = new Map(picked.value);
  if (next.has(party.uid)) next.delete(party.uid);
  else next.set(party.uid, party);
  picked.value = next;
}

/**
 * A picked party as a recipient in `role`. A legal entity signs through its
 * default (or only) representative; with neither the card asks.
 */
function toSignee(party: ContractualParty, role: SigningRequestSigneeRole): SignatureSigneeForm {
  return {
    party,
    representative: preselectedRepresentative(party),
    role,
    signature_type: SignatureType.ELECTRONIC_SIGNATURE,
  };
}

function confirmPicker(): void {
  const role = pickerRole.value;
  const additions = [...picked.value.values()].map(party => toSignee(party, role));

  if (additions.length > 0) updateForm([...props.form, ...additions]);

  pickerVisible.value = false;
  picked.value = new Map();
}

// ── Signing representative (legal entities) ─────────────────────────────────

const REPRESENTATIVE_ACTIONS = [
  { key: 'link', label: 'Add existing person' },
  { key: 'create', label: 'Create new person' },
];

function handleRepresentativeAction(key: string, index: number): void {
  if (key === 'link') openLinkRepresentative(index);
  else openCreateRepresentative(index);
}

/** The "Signs through" options: only the entity's own representatives. */
function representativeOptions(party: ContractualParty): { label: string; value: string }[] {
  if (party.kind !== 'legal_entity') return [];
  return (party.representatives ?? []).map(person => ({ label: person.name, value: person.uid }));
}

// ── Link an existing person as a representative (and signer) ────────────────

const linkIndex = ref<number | null>(null);
const linkPersonUid = ref<string | null>(null);
const linking = ref(false);
const linkParty = computed(() =>
  linkIndex.value === null ? null : (props.form[linkIndex.value]?.party ?? null)
);

/** Natural persons who aren't this entity's representatives yet. */
const linkCandidates = computed<GaiaRemoteFetch>(() => {
  const searchPeople = searchPartiesOfKind('natural_person');
  const party = linkParty.value;
  const linked = new Set(
    party?.kind === 'legal_entity' ? (party.representatives ?? []).map(p => p.uid) : []
  );
  return async (text, page): Promise<GaiaRemotePage> => {
    const result = await searchPeople(text, page);
    return { ...result, options: result.options.filter(o => !linked.has(String(o.value))) };
  };
});

function openLinkRepresentative(index: number): void {
  linkIndex.value = index;
  linkPersonUid.value = null;
}

async function confirmLinkRepresentative(): Promise<void> {
  if (linkIndex.value === null || !linkPersonUid.value) return;
  linking.value = true;
  await chooseRepresentative(linkIndex.value, linkPersonUid.value);
  linking.value = false;
  linkIndex.value = null;
}

/** Re-read a party (its representatives may have changed) and point every row at it. */
async function refreshParty(partyUid: string, representativeUid?: string): Promise<void> {
  const { data } = await getContractManagementContractualParty({
    path: { uid: partyUid },
    throwOnError: true,
  });
  updateForm(
    props.form.map(signee => {
      if (signee.party?.uid !== partyUid) return signee;
      const repUid = representativeUid ?? signee.representative?.uid;
      const representative =
        data.kind === 'legal_entity'
          ? (data.representatives?.find(person => person.uid === repUid) ?? null)
          : null;
      return { ...signee, party: data, representative };
    })
  );
}

async function chooseRepresentative(index: number, personUid: string | null): Promise<void> {
  const party = props.form[index].party;
  if (party?.kind !== 'legal_entity') return;
  if (!personUid) {
    updateSignee(index, { representative: null });
    return;
  }
  const existing = party.representatives?.find(person => person.uid === personUid);
  if (existing) {
    updateSignee(index, { representative: existing });
    return;
  }
  try {
    await addContractualPartyRepresentative({
      query: { legal_entity_uid: party.uid, representative_uid: personUid },
      throwOnError: true,
    });
    await refreshParty(party.uid, personUid);
    message.success(`Linked as a representative of ${party.name}.`);
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to link the representative.'));
  }
}

// ── Party create / edit (the Contractual Parties drawer) ────────────────────

const formVisible = ref(false);
const formRow = ref<EntityRow | null>(null);
const formDefaults = ref<EntityFormValues>({});
/** Every party created here has a preset kind, so the drawer names that kind. */
const formLabel = computed(() => {
  const kind = String(formDefaults.value.kind);
  return kind in PARTY_KIND_LABELS ? PARTY_KIND_LABELS[kind] : '';
});
/** Fields this flow has decided for the user, and labels for preset links. */
const formLockedKeys = ref<string[]>([]);
const formDefaultOptions = ref<Record<string, SelectOption[]>>({});
/** What a save means here: pick the new party, set a representative, refresh rows. */
type AfterSave = (row: EntityRow | undefined) => Promise<void>;
let afterSave: AfterSave | null = null;

function openCreateParty(): void {
  formRow.value = null;
  formDefaults.value = { kind: activePickerTab.value };
  // Created from the person or company tab, so its kind is that tab's.
  formLockedKeys.value = ['kind'];
  formDefaultOptions.value = {};
  afterSave = async (row): Promise<void> => {
    const pool = activePickerTab.value === 'natural_person' ? people : companies;
    if (row) {
      // Whoever creates a party here means to add it, so it goes straight onto
      // the request. With nothing else ticked the picker has done its job;
      // otherwise it stays open on the rest of the selection.
      updateForm([...props.form, toSignee(row as ContractualParty, pickerRole.value)]);
      if (picked.value.size === 0) {
        pickerVisible.value = false;
        return;
      }
    }
    await pool.fetch();
  };
  formVisible.value = true;
}

/** A new person who represents this row's legal entity, and signs for it. */
function openCreateRepresentative(index: number): void {
  const party = props.form[index].party;
  if (!party) return;
  formRow.value = null;
  formDefaults.value = { kind: 'natural_person', represented_uids: [party.uid] };
  // A representative is a person, and represents this company — both fixed.
  formLockedKeys.value = ['kind', 'represented_uids'];
  formDefaultOptions.value = { represented_uids: [{ label: party.name, value: party.uid }] };
  afterSave = async (row): Promise<void> => {
    if (row) await refreshParty(party.uid, row.uid);
  };
  formVisible.value = true;
}

/**
 * Fix the person who signs. The full record is fetched first: the drawer
 * re-syncs a person's representative links from what it was opened with, and
 * a partial record would read as "represents nobody".
 */
async function openEditSigner(index: number): Promise<void> {
  const signee = props.form[index];
  const signerUid =
    signee.party?.kind === 'natural_person' ? signee.party.uid : signee.representative?.uid;
  if (!signerUid || !signee.party) return;
  const partyUid = signee.party.uid;
  try {
    const { data } = await getContractManagementContractualParty({
      path: { uid: signerUid },
      throwOnError: true,
    });
    formRow.value = toPartyRow(data);
    formDefaults.value = {};
    formLockedKeys.value = [];
    formDefaultOptions.value = {};
    afterSave = (): Promise<void> => refreshParty(partyUid);
    formVisible.value = true;
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to load the party.'));
  }
}

async function handleFormSaved(row?: EntityRow): Promise<void> {
  try {
    await afterSave?.(row);
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to refresh the recipient.'));
  }
}

// The provider's accepted-name pattern comes from the backend, so it is loaded
// before any name is judged against it.
onMounted(() => {
  void loadSignatureParticipantNamePattern();
});

const listBorder = gaiaNeutral.borderCard;
const rowDivider = gaiaNeutral.borderDivider;
const rowHover = gaiaNeutral.surfaceSoft;
const dropOutline = gaiaNeutral.borderHover;
const stepInk = gaiaNeutral.ink;
const stepRing = gaiaNeutral.border;
const stepSurface = gaiaNeutral.surface;
const alertInk = gaiaStatus.alert;
</script>

<template>
  <n-flex vertical :size="28" :wrap="false">
    <!-- One group per role: its header says what the group does and, for
         signers and approvers, whether they get it at once or in turn. -->
    <n-flex v-for="section in SECTIONS" :key="section.role" vertical :size="10" :wrap="false">
      <n-flex align="flex-end" justify="space-between" :size="16" :wrap="false">
        <n-flex vertical :size="2" :wrap="false" :style="{ minWidth: 0 }">
          <n-flex align="baseline" :size="8" :wrap="false">
            <gaia-text size="body" weight="semibold" tone="ink">{{ section.title }}</gaia-text>
            <gaia-text v-if="rowsFor(section.role).length" size="meta" tone="faint">
              {{ rowsFor(section.role).length }}
            </gaia-text>
          </n-flex>
          <gaia-text size="meta" tone="subtle">{{ sectionBlurb(section) }}</gaia-text>
        </n-flex>
        <gaia-segmented
          v-if="section.orderable && rowsFor(section.role).length > 1"
          :value="isOrdered(section.role) ? 'sequential' : 'parallel'"
          :options="orderOptions(section.role)"
          @update:value="(mode: string) => setOrderMode(section.role, mode)"
        />
      </n-flex>

      <!-- Every recipient has the same shape: who, then one line of how they
           sign (or what is missing), then the level and a ⋯ for the rest. -->
      <div
        v-if="rowsFor(section.role).length"
        class="recipient-list"
        :class="{ ordered: isOrdered(section.role) }"
      >
        <div
          v-for="({ signee, index }, position) in rowsFor(section.role)"
          :key="index"
          data-tour="signing.signee-card"
          class="recipient-row"
          :class="{ 'drag-target': isDragTarget(signee.role, index) }"
          :draggable="isDraggable(signee.role)"
          @dragstart="event => handleDragStart(signee.role, index, event)"
          @dragover="event => handleDragOver(signee.role, index, event)"
          @drop="event => handleDrop(signee.role, index, event)"
        >
          <!-- In order: a numbered step on a rail; at once: the person. -->
          <div class="lead">
            <span v-if="isOrdered(signee.role)" class="step">{{ position + 1 }}</span>
            <gaia-avatar v-else :size="32">{{ initialsOf(signee.party?.name ?? '') }}</gaia-avatar>
            <span
              v-if="isOrdered(signee.role) && position < rowsFor(section.role).length - 1"
              class="rail"
            />
          </div>

          <n-flex vertical :size="4" :wrap="false" :style="{ flex: 1, minWidth: 0 }">
            <gaia-text size="dense" weight="semibold" tone="ink" truncate>
              {{ signee.party?.name || 'Unnamed party' }}
            </gaia-text>

            <!-- A company with nobody to sign for it: ask, right here. -->
            <n-flex
              v-if="isLegalEntity(signee) && signee.party && !signee.representative"
              align="center"
              :size="8"
            >
              <n-icon :component="IconAlert" :size="14" :color="alertInk" />
              <gaia-text size="meta" tone="alert">Choose who signs for it</gaia-text>
              <gaia-select
                v-if="representativeOptions(signee.party).length"
                :value="null"
                :options="representativeOptions(signee.party)"
                size="tiny"
                placeholder="Pick a representative"
                :style="{ width: '200px' }"
                @update:value="(uid: string | null) => chooseRepresentative(index, uid)"
              />
              <gaia-dropdown
                v-if="!representativeAddBlock(signee)"
                :options="REPRESENTATIVE_ACTIONS"
                placement="bottom-start"
                @select="(key: string) => handleRepresentativeAction(key, index)"
              >
                <gaia-link>Add a person</gaia-link>
              </gaia-dropdown>
              <gaia-text
                v-else-if="!representativeOptions(signee.party).length"
                size="meta"
                tone="subtle"
              >
                You can’t add representatives to {{ signee.party.name }} — ask someone with edit
                access.
              </gaia-text>
            </n-flex>

            <!-- A company signs through a person: who, and where they get it. -->
            <n-flex
              v-else-if="isLegalEntity(signee) && signee.party"
              align="center"
              :size="6"
              :wrap="false"
              :style="{ minWidth: 0 }"
            >
              <gaia-text size="meta" tone="subtle" :style="{ flex: 'none' }"
                >Signs through</gaia-text
              >
              <gaia-select
                :value="signee.representative?.uid ?? null"
                :options="representativeOptions(signee.party)"
                size="tiny"
                :consistent-menu-width="false"
                :style="{ width: '180px', flex: 'none' }"
                @update:value="(uid: string | null) => chooseRepresentative(index, uid)"
              />
              <gaia-dropdown
                :options="REPRESENTATIVE_ACTIONS"
                placement="bottom-start"
                :disabled="representativeAddBlock(signee) !== null"
                @select="(key: string) => handleRepresentativeAction(key, index)"
              >
                <gaia-icon-button
                  :dimension="22"
                  quaternary
                  :title="representativeAddBlock(signee) ?? 'Add a representative'"
                  :disabled="representativeAddBlock(signee) !== null"
                >
                  <n-icon :component="IconAdd" :size="14" />
                </gaia-icon-button>
              </gaia-dropdown>
              <gaia-text v-if="signerEmail(signee)" size="meta" tone="subtle" truncate>
                · {{ signerEmail(signee) }}
              </gaia-text>
            </n-flex>

            <gaia-text v-else size="meta" tone="subtle" truncate>
              {{ signerEmail(signee) || 'No email on record' }}
            </gaia-text>

            <n-flex v-if="rowIssue(signee)" align="center" :size="6">
              <n-icon :component="IconAlert" :size="14" :color="alertInk" />
              <gaia-text size="meta" tone="alert">{{ rowIssue(signee) }}.</gaia-text>
              <gaia-link v-if="!signerEditBlock(signee)" @click="openEditSigner(index)"
                >Add details</gaia-link
              >
              <gaia-text v-else size="meta" tone="subtle">
                You can view {{ signeeSigner(signee)?.name }} but not edit them — ask someone with
                edit access.
              </gaia-text>
            </n-flex>
          </n-flex>

          <n-flex align="center" :size="6" :wrap="false" :style="{ flex: 'none' }">
            <gaia-dropdown
              v-if="signee.role === SigningRequestSigneeRole.SIGNER"
              :options="signatureTypeMenu(signee.signature_type)"
              placement="bottom-end"
              @select="(type: SignatureType) => updateSignee(index, { signature_type: type })"
            >
              <gaia-button
                size="tiny"
                quaternary
                icon-placement="right"
                :title="`Signature level: ${signatureTypeMap[signee.signature_type]}`"
              >
                {{ SIGNATURE_LEVEL[signee.signature_type] }}
                <template #icon><n-icon :component="IconChevronDown" /></template>
              </gaia-button>
            </gaia-dropdown>
            <n-icon
              v-if="isOrdered(signee.role)"
              :component="IconReorder"
              :size="16"
              class="grip"
              title="Drag to reorder"
            />
            <gaia-dropdown
              :options="rowMenu(index, position, rowsFor(section.role).length)"
              placement="bottom-end"
              @select="(key: string) => handleRowMenu(key, index)"
            >
              <gaia-icon-button :dimension="28" quaternary title="More">
                <n-icon :component="IconMore" :size="16" />
              </gaia-icon-button>
            </gaia-dropdown>
          </n-flex>
        </div>
      </div>

      <div>
        <gaia-add-button
          :subject="signeeRoleMap[section.role].toLowerCase()"
          :data-tour="section.tourKey"
          @click="openPicker(section.role)"
        />
      </div>
    </n-flex>

    <!-- The recipient picker: the organisation's own entities, in two pools. -->
    <gaia-modal
      v-model:show="pickerVisible"
      width="640px"
      :title="pickerTitle"
      class="recipient-picker-modal"
    >
      <n-flex vertical :size="14" :wrap="false">
        <gaia-tabs v-model:value="activePickerTab" type="line">
          <n-tab-pane
            v-for="pool in [
              { kind: 'natural_person', tab: 'Natural persons', state: people },
              { kind: 'legal_entity', tab: 'Legal entities', state: companies },
            ] as const"
            :key="pool.kind"
            :name="pool.kind"
            :tab="pool.tab"
          >
            <n-flex vertical :size="10" :wrap="false">
              <n-flex align="center" :size="10" :wrap="false">
                <gaia-search-input
                  v-model:value="pool.state.query.value"
                  variant="fill"
                  placeholder="Search name or email"
                />
                <gaia-create-button
                  :subject="pool.kind === 'natural_person' ? 'natural person' : 'legal entity'"
                  variant="default"
                  @click="openCreateParty"
                />
              </n-flex>
              <gaia-banner v-if="pool.state.error.value" type="error">
                {{ pool.state.error.value }}
              </gaia-banner>
              <gaia-text
                v-else-if="!pool.state.loading.value && pool.state.results.value.length === 0"
                size="body"
                tone="tertiary"
              >
                No {{ pool.tab.toLowerCase() }} match that search.
              </gaia-text>
              <n-flex v-else vertical :size="0" :wrap="false" class="picker-list">
                <gaia-picker-option
                  v-for="party in pool.state.results.value"
                  :key="party.uid"
                  checkbox
                  data-tour="signing.picker-row"
                  :label="party.name || 'Unnamed party'"
                  :meta="pickerRowMeta(party)"
                  :initials="initialsOf(party.name)"
                  :selected="picked.has(party.uid)"
                  :disabled="isAlreadyInRole(party.uid) || isInOtherRole(party.uid)"
                  @select="togglePicked(party)"
                />
              </n-flex>
              <gaia-pagination
                v-model:page="pool.state.page.value"
                :page-size="PAGE_SIZE"
                :total="pool.state.total.value"
                :page-sizes="[]"
              />
            </n-flex>
          </n-tab-pane>
        </gaia-tabs>
      </n-flex>

      <template #action>
        <n-flex align="center" :size="8" :wrap="false">
          <gaia-text size="control" tone="subtle" :style="{ marginLeft: 'auto' }">
            {{ pickedCount }} selected
          </gaia-text>
          <gaia-button @click="pickerVisible = false">Cancel</gaia-button>
          <gaia-add-button
            variant="primary"
            size="md"
            :icon="false"
            data-tour="signing.picker-add"
            :disabled="pickedCount === 0"
            @click="confirmPicker"
          >
            Add to request
          </gaia-add-button>
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Link an existing natural person as a representative, who then signs. -->
    <gaia-modal
      :show="linkIndex !== null"
      width="460px"
      :title="`Add a representative of ${linkParty?.name ?? ''}`"
      @update:show="(open: boolean) => !open && (linkIndex = null)"
    >
      <n-flex vertical :size="10" :wrap="false">
        <gaia-text size="control" tone="text">
          Pick a natural person. They're linked as a representative and sign for this entity.
        </gaia-text>
        <gaia-remote-select
          v-model:value="linkPersonUid"
          :fetch="linkCandidates"
          prime-on-mount
          placeholder="Search people"
        />
      </n-flex>
      <template #action>
        <n-flex justify="end" :size="8">
          <gaia-button @click="linkIndex = null">Cancel</gaia-button>
          <gaia-button
            type="primary"
            :disabled="!linkPersonUid"
            :loading="linking"
            @click="confirmLinkRepresentative"
          >
            Link and sign
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Create or fix a party in the Contractual Parties drawer. -->
    <entity-form-dialog
      v-model:show="formVisible"
      :config="signingPartyConfig"
      :row="formRow"
      :row-name="formRow ? String(formRow.name ?? '') : ''"
      :label="formLabel"
      :defaults="formDefaults"
      :locked-keys="formLockedKeys"
      :default-options="formDefaultOptions"
      @saved="handleFormSaved"
    />
  </n-flex>
</template>

<style scoped>
/* The recipient list: one bordered surface with hairlines between rows, a
   numbered rail when the group signs in order, and the drag cues. Naive has no
   list-group, step-rail or drag-and-drop primitive, so these are ours. */
.recipient-list {
  border: 1px solid v-bind(listBorder);
  border-radius: 10px;
  overflow: hidden;
}

.recipient-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 12px 12px 14px;
}

.recipient-row + .recipient-row {
  border-top: 1px solid v-bind(rowDivider);
}

.recipient-row:hover {
  background: v-bind(rowHover);
}

.recipient-row.drag-target {
  outline: 2px dashed v-bind(dropOutline);
  outline-offset: -4px;
}

.recipient-list.ordered .recipient-row {
  cursor: grab;
}

/* In order, the rail is what ties the rows together: hairlines across it
   would cut the sequence into boxes. */
.recipient-list.ordered .recipient-row + .recipient-row {
  border-top: 0;
}

/* The lead column stretches the row's height, so the rail can run from one
   step's circle down into the next row's. */
.lead {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 32px;
  flex: none;
}

.step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-top: 2px;
  border: 1px solid v-bind(stepRing);
  border-radius: 50%;
  background: v-bind(stepSurface);
  color: v-bind(stepInk);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.rail {
  flex: 1;
  width: 1px;
  /* Down to the next step's circle: this row's bottom padding (12) + the next
     row's top padding (12) + the circle's offset (2). Raised so the next row's
     hover wash doesn't paint over it. */
  margin-bottom: -26px;
  position: relative;
  z-index: 1;
  background: v-bind(stepRing);
}

.grip {
  opacity: 0.45;
}

.recipient-row:hover .grip {
  opacity: 0.8;
}

.picker-list {
  max-height: 300px;
  overflow-y: auto;
}
</style>
